/**
 * OnboardingWizard - First-run setup for Electron
 *
 * Full-screen modal wizard that walks users through:
 * 1. Welcome — detected user name, overview
 * 2. Database — PostgreSQL + Ollama config (reuses ArchiveSetupPanel pattern)
 * 3. File Locations — media dir, backup dir, archive/export path
 * 4. Confirmation — summary of all settings
 *
 * @module @humanizer/studio/components/onboarding/OnboardingWizard
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { createLogger } from '../../utils/debug';

const log = createLogger('Onboarding');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface OnboardingWizardProps {
  onComplete: () => void;
}

interface AdminForm {
  host: string;
  port: number;
  user: string;
  password: string;
}

interface CustomForm {
  host: string;
  port: number;
  database: string;
  user: string;
  password: string;
}

interface ProvisionStep {
  name: string;
  success: boolean;
  error?: string;
}

interface OllamaForm {
  url: string;
}

interface PathsForm {
  mediaRoot: string;
  backupDir: string;
  archivePath: string;
}

type DbMode = 'auto' | 'custom';
type DbStatus = 'idle' | 'provisioning' | 'testing' | 'success' | 'error';

const STEP_COUNT = 4;

// ═══════════════════════════════════════════════════════════════════════════
// STEP COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function StepWelcome({ userName }: { userName: string }) {
  return (
    <>
      <div className="onboarding__header">
        <div className="onboarding__logo">humanizer</div>
        <h2 className="onboarding__title">
          Welcome{userName ? `, ${userName}` : ''}
        </h2>
        <p className="onboarding__subtitle">
          Let's set up where your data will live. Sensible defaults are
          pre-filled — change only what you need.
        </p>
      </div>

      <div className="onboarding__section">
        <div className="onboarding__section-header">
          <span className="onboarding__section-title">What we'll configure</span>
        </div>
        <ul className="onboarding__checklist">
          <li>PostgreSQL database connection</li>
          <li>Ollama AI model server (optional)</li>
          <li>Media, backup, and export file locations</li>
        </ul>
      </div>
    </>
  );
}

function StepDatabase({
  admin, setAdmin,
  custom, setCustom,
  ollama, setOllama,
  dbMode, setDbMode,
  dbStatus, dbError,
  provisionSteps,
  onProvision,
  onTestCustom,
}: {
  admin: AdminForm;
  setAdmin: React.Dispatch<React.SetStateAction<AdminForm>>;
  custom: CustomForm;
  setCustom: React.Dispatch<React.SetStateAction<CustomForm>>;
  ollama: OllamaForm;
  setOllama: React.Dispatch<React.SetStateAction<OllamaForm>>;
  dbMode: DbMode;
  setDbMode: React.Dispatch<React.SetStateAction<DbMode>>;
  dbStatus: DbStatus;
  dbError: string | null;
  provisionSteps: ProvisionStep[];
  onProvision: () => void;
  onTestCustom: () => void;
}) {
  return (
    <>
      <div className="onboarding__header">
        <h2 className="onboarding__title">Database Connection</h2>
        <p className="onboarding__subtitle">
          Humanizer stores your content archive in a local PostgreSQL database.
          {dbMode === 'auto' && ' A dedicated database user will be created automatically.'}
        </p>
      </div>

      {dbError && (
        <div className="onboarding__error">
          <span>&#9888;</span>
          <span>{dbError}</span>
        </div>
      )}

      {/* PostgreSQL — Auto Provision Mode */}
      {dbMode === 'auto' && (
        <div className="onboarding__section">
          <div className="onboarding__section-header">
            <span className="onboarding__section-title">PostgreSQL Setup</span>
            {dbStatus === 'success' && (
              <span className="onboarding__section-status onboarding__section-status--success">Ready</span>
            )}
            {dbStatus === 'error' && (
              <span className="onboarding__section-status onboarding__section-status--error">Failed</span>
            )}
          </div>

          {/* Before provisioning: show admin form */}
          {dbStatus !== 'success' && (
            <>
              <p className="onboarding__subtitle onboarding__subtitle--spaced">
                Enter your PostgreSQL admin credentials. These are used once to create a
                dedicated <code>humanizer</code> database user, then discarded.
              </p>

              <div className="onboarding__form-row">
                <label className="onboarding__label">
                  Host
                  <input
                    type="text"
                    className="onboarding__input"
                    value={admin.host}
                    onChange={(e) => setAdmin(p => ({ ...p, host: e.target.value }))}
                    placeholder="localhost"
                    disabled={dbStatus === 'provisioning'}
                  />
                </label>
                <label className="onboarding__label onboarding__label--small">
                  Port
                  <input
                    type="number"
                    className="onboarding__input"
                    value={admin.port}
                    onChange={(e) => setAdmin(p => ({ ...p, port: parseInt(e.target.value) || 5432 }))}
                    placeholder="5432"
                    disabled={dbStatus === 'provisioning'}
                  />
                </label>
              </div>

              <div className="onboarding__form-row">
                <label className="onboarding__label">
                  Admin User
                  <input
                    type="text"
                    className="onboarding__input"
                    value={admin.user}
                    onChange={(e) => setAdmin(p => ({ ...p, user: e.target.value }))}
                    placeholder="postgres"
                    disabled={dbStatus === 'provisioning'}
                  />
                </label>
              </div>

              <div className="onboarding__form-row">
                <label className="onboarding__label">
                  Admin Password
                  <input
                    type="password"
                    className="onboarding__input"
                    value={admin.password}
                    onChange={(e) => setAdmin(p => ({ ...p, password: e.target.value }))}
                    placeholder="(empty for Postgres.app)"
                    disabled={dbStatus === 'provisioning'}
                  />
                </label>
              </div>

              <div className="onboarding__form-actions">
                <button
                  className="onboarding__btn onboarding__btn--primary"
                  onClick={onProvision}
                  disabled={dbStatus === 'provisioning'}
                >
                  {dbStatus === 'provisioning' ? 'Setting up...' : 'Set Up Database'}
                </button>
              </div>
            </>
          )}

          {/* Provisioning progress */}
          {provisionSteps.length > 0 && (
            <div className="onboarding__provision-steps">
              {provisionSteps.map((s, i) => (
                <div key={i} className={`onboarding__provision-step ${
                  s.success ? 'onboarding__provision-step--ok' :
                  s.error ? 'onboarding__provision-step--fail' : ''
                }`}>
                  <span className="onboarding__provision-icon">
                    {s.success ? '\u2713' : s.error ? '\u2717' : '\u2022'}
                  </span>
                  <span>{s.name}</span>
                  {s.error && !s.success && (
                    <span className="onboarding__provision-error">{s.error}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* After success: summary */}
          {dbStatus === 'success' && (
            <div className="onboarding__help">
              <p>
                <strong>Connected as:</strong>{' '}
                <code>humanizer@{admin.host}:{admin.port}/humanizer_archive</code>
              </p>
              <p className="mt-xs">
                Password stored securely in your system keychain.
              </p>
            </div>
          )}

          <div className="onboarding__help">
            <p>
              <strong>Need PostgreSQL?</strong> Install{' '}
              <a href="https://postgresapp.com" target="_blank" rel="noopener noreferrer">Postgres.app</a>
              {' '}(macOS) or{' '}
              <a href="https://www.postgresql.org/download/" target="_blank" rel="noopener noreferrer">PostgreSQL</a>
              . The <code>pgvector</code> extension will be enabled automatically.
            </p>
          </div>
        </div>
      )}

      {/* PostgreSQL — Custom Credentials Mode */}
      {dbMode === 'custom' && (
        <div className="onboarding__section">
          <div className="onboarding__section-header">
            <span className="onboarding__section-title">PostgreSQL (Custom)</span>
            {dbStatus === 'success' && (
              <span className="onboarding__section-status onboarding__section-status--success">Connected</span>
            )}
            {dbStatus === 'error' && (
              <span className="onboarding__section-status onboarding__section-status--error">Failed</span>
            )}
          </div>

          <div className="onboarding__form-row">
            <label className="onboarding__label">
              Host
              <input
                type="text"
                className="onboarding__input"
                value={custom.host}
                onChange={(e) => setCustom(p => ({ ...p, host: e.target.value }))}
                placeholder="localhost"
              />
            </label>
            <label className="onboarding__label onboarding__label--small">
              Port
              <input
                type="number"
                className="onboarding__input"
                value={custom.port}
                onChange={(e) => setCustom(p => ({ ...p, port: parseInt(e.target.value) || 5432 }))}
                placeholder="5432"
              />
            </label>
          </div>

          <div className="onboarding__form-row">
            <label className="onboarding__label">
              Database
              <input
                type="text"
                className="onboarding__input"
                value={custom.database}
                onChange={(e) => setCustom(p => ({ ...p, database: e.target.value }))}
                placeholder="humanizer_archive"
              />
            </label>
          </div>

          <div className="onboarding__form-row">
            <label className="onboarding__label">
              User
              <input
                type="text"
                className="onboarding__input"
                value={custom.user}
                onChange={(e) => setCustom(p => ({ ...p, user: e.target.value }))}
                placeholder="postgres"
              />
            </label>
          </div>

          <div className="onboarding__form-row">
            <label className="onboarding__label">
              Password
              <input
                type="password"
                className="onboarding__input"
                value={custom.password}
                onChange={(e) => setCustom(p => ({ ...p, password: e.target.value }))}
                placeholder="(optional)"
              />
            </label>
          </div>

          <div className="mt-sm">
            <button
              className="onboarding__btn"
              onClick={onTestCustom}
              disabled={dbStatus === 'testing'}
            >
              {dbStatus === 'testing' ? 'Testing...' : 'Test Connection'}
            </button>
          </div>
        </div>
      )}

      {/* Mode toggle */}
      <button
        className="onboarding__toggle-advanced"
        onClick={() => {
          setDbMode(dbMode === 'auto' ? 'custom' : 'auto');
          // Reset status when switching modes
        }}
        type="button"
      >
        {dbMode === 'auto' ? 'Use custom credentials' : 'Use automatic setup'}
      </button>

      {/* Ollama */}
      <div className="onboarding__section">
        <div className="onboarding__section-header">
          <span className="onboarding__section-title">Ollama (optional)</span>
        </div>

        <div className="onboarding__form-row">
          <label className="onboarding__label">
            Ollama URL
            <input
              type="text"
              className="onboarding__input"
              value={ollama.url}
              onChange={(e) => setOllama(o => ({ ...o, url: e.target.value }))}
              placeholder="http://localhost:11434"
            />
          </label>
        </div>

        <div className="onboarding__help">
          <p>
            <strong>Optional:</strong> Install{' '}
            <a href="https://ollama.com" target="_blank" rel="noopener noreferrer">Ollama</a>
            {' '}for local AI features (humanization, embeddings, vision).
          </p>
        </div>
      </div>
    </>
  );
}

function StepPaths({
  paths, setPaths,
  onBrowse,
}: {
  paths: PathsForm;
  setPaths: React.Dispatch<React.SetStateAction<PathsForm>>;
  onBrowse: (field: keyof PathsForm) => void;
}) {
  return (
    <>
      <div className="onboarding__header">
        <h2 className="onboarding__title">File Locations</h2>
        <p className="onboarding__subtitle">
          Choose where Humanizer stores media files, backups, and exports.
          Directories will be created automatically if they don't exist.
        </p>
      </div>

      <div className="onboarding__section">
        <div className="onboarding__section-header">
          <span className="onboarding__section-title">Media Directory</span>
        </div>
        <p className="onboarding__subtitle onboarding__subtitle--compact">
          Images, audio, and video extracted from your imported archives.
        </p>
        <div className="onboarding__path-row">
          <label className="onboarding__label">
            Path
            <input
              type="text"
              className="onboarding__input"
              value={paths.mediaRoot}
              onChange={(e) => setPaths(p => ({ ...p, mediaRoot: e.target.value }))}
            />
          </label>
          <button
            className="onboarding__browse-btn"
            onClick={() => onBrowse('mediaRoot')}
            type="button"
          >
            Browse
          </button>
        </div>
      </div>

      <div className="onboarding__section">
        <div className="onboarding__section-header">
          <span className="onboarding__section-title">Backup Directory</span>
        </div>
        <p className="onboarding__subtitle onboarding__subtitle--compact">
          pg_dump backups of your archive database (auto-rotated, last 5 kept).
        </p>
        <div className="onboarding__path-row">
          <label className="onboarding__label">
            Path
            <input
              type="text"
              className="onboarding__input"
              value={paths.backupDir}
              onChange={(e) => setPaths(p => ({ ...p, backupDir: e.target.value }))}
            />
          </label>
          <button
            className="onboarding__browse-btn"
            onClick={() => onBrowse('backupDir')}
            type="button"
          >
            Browse
          </button>
        </div>
      </div>

      <div className="onboarding__section">
        <div className="onboarding__section-header">
          <span className="onboarding__section-title">Archive / Export Path</span>
        </div>
        <p className="onboarding__subtitle onboarding__subtitle--compact">
          Optional: folder containing your ChatGPT/Claude export. Used for media URL resolution.
        </p>
        <div className="onboarding__path-row">
          <label className="onboarding__label">
            Path
            <input
              type="text"
              className="onboarding__input"
              value={paths.archivePath}
              onChange={(e) => setPaths(p => ({ ...p, archivePath: e.target.value }))}
              placeholder="(optional)"
            />
          </label>
          <button
            className="onboarding__browse-btn"
            onClick={() => onBrowse('archivePath')}
            type="button"
          >
            Browse
          </button>
        </div>
      </div>
    </>
  );
}

function StepConfirmation({
  dbMode,
  admin,
  custom,
  ollama,
  paths,
}: {
  dbMode: DbMode;
  admin: AdminForm;
  custom: CustomForm;
  ollama: OllamaForm;
  paths: PathsForm;
}) {
  const dbHost = dbMode === 'auto' ? admin.host : custom.host;
  const dbPort = dbMode === 'auto' ? admin.port : custom.port;
  const dbName = dbMode === 'auto' ? 'humanizer_archive' : custom.database;
  const dbUser = dbMode === 'auto' ? 'humanizer' : custom.user;

  return (
    <>
      <div className="onboarding__header">
        <h2 className="onboarding__title">Ready to go</h2>
        <p className="onboarding__subtitle">
          Review your configuration. You can change any of these later in Settings.
        </p>
      </div>

      <div className="onboarding__section">
        <div className="onboarding__summary">
          <div className="onboarding__summary-row">
            <span className="onboarding__summary-label">Database</span>
            <span className="onboarding__summary-value">
              {dbHost}:{dbPort}/{dbName}
            </span>
          </div>
          <div className="onboarding__summary-row">
            <span className="onboarding__summary-label">DB User</span>
            <span className="onboarding__summary-value">{dbUser}</span>
          </div>
          {dbMode === 'auto' && (
            <div className="onboarding__summary-row">
              <span className="onboarding__summary-label">Password</span>
              <span className="onboarding__summary-value">Stored in system keychain</span>
            </div>
          )}
          <div className="onboarding__summary-row">
            <span className="onboarding__summary-label">Ollama</span>
            <span className="onboarding__summary-value">{ollama.url}</span>
          </div>
          <div className="onboarding__summary-row">
            <span className="onboarding__summary-label">Media</span>
            <span className="onboarding__summary-value">{paths.mediaRoot}</span>
          </div>
          <div className="onboarding__summary-row">
            <span className="onboarding__summary-label">Backups</span>
            <span className="onboarding__summary-value">{paths.backupDir}</span>
          </div>
          {paths.archivePath && (
            <div className="onboarding__summary-row">
              <span className="onboarding__summary-label">Archive</span>
              <span className="onboarding__summary-value">{paths.archivePath}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN WIZARD
// ═══════════════════════════════════════════════════════════════════════════

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [userName, setUserName] = useState('');

  // Form state — auto provisioning mode
  const [admin, setAdmin] = useState<AdminForm>({
    host: 'localhost',
    port: 5432,
    user: 'postgres',
    password: '',
  });

  // Form state — custom credentials mode
  const [custom, setCustom] = useState<CustomForm>({
    host: 'localhost',
    port: 5432,
    database: 'humanizer_archive',
    user: 'postgres',
    password: '',
  });

  const [ollama, setOllama] = useState<OllamaForm>({
    url: 'http://localhost:11434',
  });

  const [paths, setPaths] = useState<PathsForm>({
    mediaRoot: '',
    backupDir: '',
    archivePath: '',
  });

  const [dbMode, setDbMode] = useState<DbMode>('auto');
  const [dbStatus, setDbStatus] = useState<DbStatus>('idle');
  const [dbError, setDbError] = useState<string | null>(null);
  const [provisionSteps, setProvisionSteps] = useState<ProvisionStep[]>([]);
  const [saving, setSaving] = useState(false);

  // Focus trap
  const containerRef = useFocusTrap({ active: true });

  // Load defaults on mount
  useEffect(() => {
    (async () => {
      try {
        // Get OS user name
        const osUser = await window.electronLocalAuth?.resolveOsUser();
        if (osUser?.displayName) {
          setUserName(osUser.displayName.split(' ')[0]); // first name
        }

        // Get default paths
        const defaults = await window.electronApp?.getDefaultPaths();
        if (defaults) {
          setPaths(p => ({
            ...p,
            mediaRoot: p.mediaRoot || defaults.mediaRoot,
            backupDir: p.backupDir || defaults.backupDir,
          }));
        }

        // Load saved archive config if any (populate both admin + custom forms)
        const savedConfig = await window.electronAPI?.store.get('archiveConfig') as { postgres?: { host?: string; port?: number; database?: string; user?: string }; ollama?: { url?: string } } | undefined;
        const pg = savedConfig?.postgres;
        if (pg) {
          setAdmin(a => ({
            ...a,
            host: pg.host || a.host,
            port: pg.port || a.port,
          }));
          setCustom(c => ({
            ...c,
            host: pg.host || c.host,
            port: pg.port || c.port,
            database: pg.database || c.database,
            user: pg.user || c.user,
          }));
        }

        // If already provisioned, show as custom mode with saved credentials
        const provisioned = await window.electronAPI?.store.get('archiveProvisioned') as boolean;
        if (provisioned && savedConfig?.postgres?.user === 'humanizer') {
          setDbStatus('success');
        }
      } catch {
        // Defaults are fine
      }
    })();
  }, []);

  // Auto provision database
  const handleProvision = useCallback(async () => {
    setDbStatus('provisioning');
    setDbError(null);
    setProvisionSteps([]);
    try {
      const result = await window.electronArchive?.provisionDatabase({
        host: admin.host,
        port: admin.port,
        user: admin.user,
        password: admin.password || undefined,
      });
      if (result?.steps) {
        setProvisionSteps(result.steps);
      }
      if (result?.success) {
        setDbStatus('success');
      } else {
        setDbStatus('error');
        setDbError(result?.error || 'Provisioning failed');
      }
    } catch (err: any) {
      setDbStatus('error');
      setDbError(err.message || 'Provisioning failed');
    }
  }, [admin]);

  // Test custom DB connection
  const handleTestCustom = useCallback(async () => {
    setDbStatus('testing');
    setDbError(null);
    try {
      const result = await window.electronArchive?.configure({
        postgres: {
          host: custom.host,
          port: custom.port,
          database: custom.database,
          user: custom.user,
          password: custom.password || undefined,
        },
        ollama: { url: ollama.url },
      });
      if (result?.success) {
        setDbStatus('success');
      } else {
        setDbStatus('error');
        setDbError(result?.error || 'Connection failed');
      }
    } catch (err: any) {
      setDbStatus('error');
      setDbError(err.message || 'Connection failed');
    }
  }, [custom, ollama]);

  // Browse for folder
  const handleBrowse = useCallback(async (field: keyof PathsForm) => {
    const selected = await window.electronAPI?.dialog.selectFolder();
    if (selected) {
      setPaths(p => ({ ...p, [field]: selected }));
    }
  }, []);

  // Finish wizard
  const handleFinish = useCallback(async () => {
    setSaving(true);
    try {
      // If custom mode and not yet connected, configure now
      if (dbMode === 'custom' && dbStatus !== 'success') {
        await window.electronArchive?.configure({
          postgres: {
            host: custom.host,
            port: custom.port,
            database: custom.database,
            user: custom.user,
            password: custom.password || undefined,
          },
          ollama: { url: ollama.url },
        });
      }

      // Configure Ollama if auto-provisioned (DB already connected)
      if (dbMode === 'auto' && dbStatus === 'success') {
        // Ollama config is stored separately — just save it via store
        const existingConfig = await window.electronAPI?.store.get('archiveConfig') as Record<string, unknown> | undefined;
        if (existingConfig) {
          existingConfig.ollama = { url: ollama.url };
          await window.electronAPI?.store.set('archiveConfig', existingConfig);
        }
      }

      // Save data paths
      await window.electronApp?.setDataPaths({
        mediaRoot: paths.mediaRoot || undefined,
        backupDir: paths.backupDir || undefined,
      });

      // Save archive export path if provided
      if (paths.archivePath) {
        await window.electronAPI?.store.set('archivePath', paths.archivePath);
      }

      // Mark onboarding complete
      await window.electronApp?.completeOnboarding();

      onComplete();
    } catch (err) {
      log.error('Failed to save:', err);
      // Still complete — user can reconfigure in Settings
      await window.electronApp?.completeOnboarding();
      onComplete();
    } finally {
      setSaving(false);
    }
  }, [dbMode, dbStatus, custom, ollama, paths, onComplete]);

  // Navigation
  const canNext = step < STEP_COUNT - 1;
  const canBack = step > 0;
  const isLastStep = step === STEP_COUNT - 1;

  return (
    <div className="onboarding__overlay" role="dialog" aria-modal="true" aria-label="Setup wizard">
      <div className="onboarding" ref={containerRef}>
        {/* Step indicator */}
        <div className="onboarding__steps" aria-label={`Step ${step + 1} of ${STEP_COUNT}`}>
          {Array.from({ length: STEP_COUNT }).map((_, i) => (
            <div
              key={i}
              className={`onboarding__step-dot ${
                i === step ? 'onboarding__step-dot--active' :
                i < step ? 'onboarding__step-dot--completed' : ''
              }`}
            />
          ))}
        </div>

        {/* Step content */}
        {step === 0 && <StepWelcome userName={userName} />}
        {step === 1 && (
          <StepDatabase
            admin={admin} setAdmin={setAdmin}
            custom={custom} setCustom={setCustom}
            ollama={ollama} setOllama={setOllama}
            dbMode={dbMode} setDbMode={setDbMode}
            dbStatus={dbStatus} dbError={dbError}
            provisionSteps={provisionSteps}
            onProvision={handleProvision}
            onTestCustom={handleTestCustom}
          />
        )}
        {step === 2 && (
          <StepPaths
            paths={paths} setPaths={setPaths}
            onBrowse={handleBrowse}
          />
        )}
        {step === 3 && (
          <StepConfirmation
            dbMode={dbMode}
            admin={admin}
            custom={custom}
            ollama={ollama}
            paths={paths}
          />
        )}

        {/* Navigation */}
        <div className="onboarding__actions">
          <div>
            {canBack && (
              <button
                className="onboarding__btn onboarding__btn--ghost"
                onClick={() => setStep(s => s - 1)}
              >
                Back
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            {step === 0 && (
              <button
                className="onboarding__btn onboarding__btn--ghost"
                onClick={async () => {
                  // Skip wizard entirely with defaults
                  await window.electronApp?.completeOnboarding();
                  onComplete();
                }}
              >
                Skip setup
              </button>
            )}

            {canNext && (
              <button
                className="onboarding__btn onboarding__btn--primary"
                onClick={() => setStep(s => s + 1)}
              >
                Next
              </button>
            )}

            {isLastStep && (
              <button
                className="onboarding__btn onboarding__btn--primary"
                onClick={handleFinish}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Start Using Humanizer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingWizard;
